import React, { useEffect, useState } from "react";
import { ArrowDown, ArrowRight2, ArrowUp, Refresh2 } from "iconsax-reactjs";
import { PERIODS, formatCount, formatSigned } from "../../utils/overviewData";
import "./overview.css";

// Shared pieces of the admin and dealer dashboards: the stat strip, its
// tiles with their 14-day bars, a per-day column chart with its table twin,
// stage bars and the period toolbar.

const MINUTE_MS = 60 * 1000;

export const dayLabel = (ms) =>
  new Date(ms).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
const shortDate = (ms) => new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

const compact = (n) => (n >= 1000 ? `${Number((n / 1000).toFixed(1))}K` : String(n));

// A clean top for the y-axis whose half is a whole number too.
function niceTop(max) {
  if (max <= 4) return 4;
  const pow = 10 ** Math.floor(Math.log10(max));
  const step = [1, 2, 4, 6, 8, 10].find((s) => s * pow >= max);
  return step * pow;
}

export function TrendBars({ counts, label }) {
  const max = Math.max(1, ...counts);
  const last = counts.length - 1;
  return (
    <span className="demand ov-trend" role="img" aria-label={`${label}: ${counts.join(", ")}`}>
      {counts.map((n, i) => (
        <i
          key={i}
          className={[n === 0 && "is-empty", i === last && "is-today"].filter(Boolean).join(" ") || undefined}
          style={n === 0 ? undefined : { height: `${Math.max(2, Math.round((n / max) * 22))}px` }}
        />
      ))}
    </span>
  );
}

export function StatStrip({ label, children }) {
  return (
    <div className="stat-strip ov-strip" role="group" aria-label={label}>
      {children}
    </div>
  );
}

/**
 * One stat tile. Only numbers with a history pass change and trend; a state
 * (leads waiting, unread chats) shows its value and a note. Every tile ends
 * with a link to the page that acts on it.
 */
export function StatTile({
  label, value, change, compareText, goodWhen = "up", note, noteTone = "muted", noteIcon,
  trend, trendLabel, linkLabel, onOpen, loading,
}) {
  const tone = !change || change.direction === "flat"
    ? "flat"
    : change.direction === goodWhen ? "good" : "bad";

  return (
    <div className="stat ov-tile">
      <span className="stat-label">{label}</span>
      {loading ? (
        <>
          <span className="skeleton ov-skel-value" />
          <span className="skeleton ov-skel-line" />
          {note && <span className="stat-note ov-note">{note}</span>}
        </>
      ) : (
        <>
          <div className="ov-value-row">
            <span className="stat-value ov-value">{typeof value === "number" ? formatCount(value) : value}</span>
            {trend && <TrendBars counts={trend} label={trendLabel || `${label}, last ${trend.length} days`} />}
          </div>
          {change && (
            <span className={`ov-change is-${tone}`}>
              {change.direction === "up" && <ArrowUp size={14} variant="Linear" aria-hidden="true" />}
              {change.direction === "down" && <ArrowDown size={14} variant="Linear" aria-hidden="true" />}
              {change.direction === "flat"
                ? `Same as ${compareText}`
                : `${formatSigned(change.diff)} vs ${compareText}`}
            </span>
          )}
          {note && (noteTone === "warning" ? (
            <span className="chip chip-warning ov-note-chip">{noteIcon}{note}</span>
          ) : (
            <span className="stat-note ov-note">{note}</span>
          ))}
        </>
      )}
      {onOpen && (
        <button type="button" className="ov-open" onClick={onOpen} aria-label={`${linkLabel} (${label})`}>
          {linkLabel}
          <ArrowRight2 size={14} variant="Linear" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/**
 * One row of a per-day column chart. Pointer and arrow keys read a day's
 * count; the latest value and the busiest day are labelled on the plot.
 */
export function DayColumns({ series, label, unit, height = 88, showDates = false, emphasizePeriod = true }) {
  const [active, setActive] = useState(null);
  const last = series.length - 1;
  const max = Math.max(0, ...series.map((day) => day.count));
  const top = niceTop(max);
  const busiest = series.findIndex((day) => day.count === max && max > 0);
  const dateMarks = [...new Set([0, Math.round(last / 4), Math.round(last / 2), Math.round((3 * last) / 4), last])];

  const move = (event) => {
    const keys = { ArrowLeft: -1, ArrowRight: 1 };
    if (event.key === "Escape") return setActive(null);
    if (event.key === "Home") return setActive(0);
    if (event.key === "End") return setActive(last);
    if (!(event.key in keys)) return undefined;
    event.preventDefault();
    return setActive((current) => Math.min(last, Math.max(0, (current ?? last) + keys[event.key])));
  };

  const point = (event) => {
    const box = event.currentTarget.getBoundingClientRect();
    const index = Math.floor(((event.clientX - box.left) / box.width) * series.length);
    setActive(Math.min(last, Math.max(0, index)));
  };

  const day = active === null ? null : series[active];
  const latest = series[last];

  return (
    <div className="ov-cols">
      <div className="ov-cols-head">
        <span className="ov-cols-title">{label}</span>
        <span className="ov-cols-sub">Per day</span>
      </div>
      <div className="ov-cols-chart" style={{ "--ov-plot-h": `${height}px` }}>
        <div className="ov-cols-ticks" aria-hidden="true">
          <span>{compact(top)}</span>
          <span>{compact(top / 2)}</span>
          <span>0</span>
        </div>
        <div
          className="ov-cols-plot"
          tabIndex={0}
          role="group"
          aria-label={`${label} per day, ${series.length} days, latest ${unit(latest.count)}. Use the arrow keys to read each day.`}
          onKeyDown={move}
          onPointerMove={point}
          onPointerLeave={() => setActive(null)}
          onBlur={() => setActive(null)}
        >
          <span className="ov-grid is-top" />
          <span className="ov-grid is-mid" />
          {max === 0 && <span className="ov-cols-empty">None in these {series.length} days</span>}
          {series.map((d, i) => {
            const share = (d.count / top) * 100;
            const labelled = d.count > 0 && (i === last || i === busiest);
            return (
              <span key={d.start} className={`ov-slot${active === i ? " is-active" : ""}`}>
                {d.count > 0 && (
                  <span
                    className={[
                      "ov-col",
                      d.isToday && "is-today",
                      emphasizePeriod && !d.inPeriod && "is-before",
                    ].filter(Boolean).join(" ")}
                    style={{ height: `${share}%` }}
                  />
                )}
                {labelled && (
                  <span className="ov-col-label" style={{ bottom: `${share}%` }}>{formatCount(d.count)}</span>
                )}
              </span>
            );
          })}
          {day && (
            <span
              className={`ov-tip${active > last / 2 ? " is-left" : ""}`}
              style={{ left: `${((active + 0.5) / series.length) * 100}%` }}
            >
              <strong>{unit(day.count)}</strong>
              <span>{day.isToday ? "Today" : dayLabel(day.start)}</span>
            </span>
          )}
        </div>
      </div>
      {showDates && (
        <div className="ov-cols-dates" aria-hidden="true">
          {dateMarks.map((i) => (
            <span
              key={i}
              className={i === 0 ? "is-first" : i === last ? "is-last" : undefined}
              style={{ left: `${((i + 0.5) / series.length) * 100}%` }}
            >
              {i === last ? "Today" : shortDate(series[i].start)}
            </span>
          ))}
        </div>
      )}
      <span className="sr-only" aria-live="polite">
        {day ? `${day.isToday ? "Today" : dayLabel(day.start)}: ${unit(day.count)}` : ""}
      </span>
    </div>
  );
}

/** The table twin of the day charts: one row per day, newest first. */
export function DayTable({ series, columns, caption }) {
  return (
    <div className="table-wrap ov-daytable">
      <table className="table">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            <th scope="col">Day</th>
            {columns.map((column) => (
              <th key={column.label} scope="col" className="num">{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {series.map((d, i) => ({ d, i })).reverse().map(({ d, i }) => (
            <tr key={d.start}>
              <td>{d.isToday ? "Today" : dayLabel(d.start)}</td>
              {columns.map((column) => (
                <td key={column.label} className="num">{formatCount(column.counts[i])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Where a set of leads stands: one bar split by stage, then a legend with
 * each stage's count. Shades step one hue from light to dark through the
 * stages; a muted row (a closed stage) is grey. The legend reflows to the
 * card's width, two or three columns, so it fills a narrow card and a full
 * row alike.
 */
export function StageMix({ rows, onOpen, label }) {
  const ramp = rows.filter((row) => !row.muted);
  const shadeOf = (row) => {
    const step = ramp.indexOf(row);
    return step >= 0 ? Math.round(40 + (60 * step) / Math.max(1, ramp.length - 1)) : 100;
  };

  return (
    <div className="ov-mix">
      <div className="ov-mix-bar" aria-hidden="true">
        {rows.filter((row) => row.count > 0).map((row) => (
          <span
            key={row.id}
            className={`ov-mix-seg${row.muted ? " is-muted" : ""}`}
            style={{ flexGrow: row.count, "--ov-shade": `${shadeOf(row)}%` }}
            title={`${row.label}: ${formatCount(row.count)}`}
          />
        ))}
      </div>
      <ul className="ov-mix-legend" aria-label={label}>
        {rows.map((row) => {
          const className = `ov-mix-item${row.muted ? " is-muted" : ""}`;
          const body = (
            <>
              <span className="ov-mix-swatch" style={{ "--ov-shade": `${shadeOf(row)}%` }} />
              <span className="ov-mix-label">{row.label}</span>
              <span className="ov-mix-count">{formatCount(row.count)}</span>
            </>
          );
          return (
            <li key={row.id}>
              {onOpen ? (
                <button
                  type="button"
                  className={className}
                  onClick={() => onOpen(row)}
                  aria-label={`${row.label}: ${formatCount(row.count)}`}
                >
                  {body}
                </button>
              ) : (
                <div className={className}>{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function updatedText(at, now) {
  const minutes = Math.floor(Math.max(0, now - at) / MINUTE_MS);
  if (minutes < 1) return "Updated just now";
  if (minutes < 60) return `Updated ${minutes} min ago`;
  return `Updated ${Math.floor(minutes / 60)} h ago`;
}

/** The one filter row over everything a dashboard shows. */
export function OverviewToolbar({ periodId, onPeriodChange, compareText, updatedAt, onRefresh, refreshing }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), MINUTE_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (updatedAt) setNow(Date.now());
  }, [updatedAt]);

  return (
    <div className="toolbar ov-toolbar">
      <div className="segmented" role="group" aria-label="Period">
        {PERIODS.map((period) => (
          <button
            key={period.id}
            type="button"
            aria-pressed={periodId === period.id}
            onClick={() => onPeriodChange(period.id)}
          >
            {period.label}
          </button>
        ))}
      </div>
      <span className="ov-compare">{compareText}</span>
      <span className="toolbar-spacer" />
      {updatedAt && <span className="ov-updated">{updatedText(updatedAt, now)}</span>}
      <button type="button" className="btn-ghost btn-sm ov-refresh" onClick={onRefresh} disabled={refreshing}>
        {refreshing
          ? <span className="btn-spinner" aria-hidden="true" />
          : <Refresh2 size={16} variant="Linear" aria-hidden="true" />}
        Refresh
      </button>
    </div>
  );
}
